import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { uploadBlob } from "../lib/media";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import AuthMedia from "../components/AuthMedia";
import FamilyTree, { CANVAS_W, CANVAS_H } from "../components/FamilyTree";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TreePine, Image as ImageIcon, Upload, Loader2, X, Save, Trash2, Paperclip, Plus, Users, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";

const TREE_BG = "https://static.prod-images.emergentagent.com/jobs/bccf4279-ffdd-41eb-83f3-c278b5ee2273/images/a287e769f60ba612f20ad9328b6c071e2b2ec0f3563edb15c91020277c890ce2.jpeg";

const RELATIONS = [
  "Padre", "Madre", "Nonno", "Nonna", "Bisnonno", "Bisnonna", "Coniuge",
  "Compagno/a", "Figlio", "Figlia", "Nipote", "Fratello", "Sorella",
  "Zio", "Zia", "Cugino", "Cugina", "Suocero", "Suocera",
];

const emptyForm = { id: null, name: "", relation: "", birth_year: "", death_year: "", bio: "", photo: null, media: [], person_heir_id: null };

const AlberoFamiglia = () => {
  const { user } = useAuth();
  const photoRef = useRef(null);
  const mediaRef = useRef(null);
  const [nodes, setNodes] = useState(null);
  const [links, setLinks] = useState([]);
  const [people, setPeople] = useState([]);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("edit");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const loadNodes = () => api.get("/ancestors").then((res) => setNodes(res.data)).catch(() => setNodes([]));
  const loadLinks = () => api.get("/family-links").then((res) => setLinks(res.data)).catch(() => setLinks([]));
  useEffect(() => { loadNodes(); loadLinks(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onAddNew = () => {
    setMode("create");
    setForm(emptyForm);
    setOpen(true);
  };

  const centerPos = () => ({
    x: Math.round(CANVAS_W / 2 - 95 + (Math.random() - 0.5) * 300),
    y: Math.round(CANVAS_H / 2 - 75 + (Math.random() - 0.5) * 220),
  });

  const openPeople = () => {
    setPeopleOpen(true);
    api.get("/people").then((res) => setPeople(res.data)).catch(() => setPeople([]));
  };

  const addFromPerson = (p) => {
    setPeopleOpen(false);
    setMode("create");
    setForm({
      ...emptyForm, name: p.name || "", relation: p.relationship || "",
      photo: p.cover_image || null, person_heir_id: p.heir_id,
    });
    setOpen(true);
  };

  const onNodeClick = (node) => {
    setMode("edit");
    setForm({
      id: node.id, name: node.name || "", relation: node.relation || "",
      birth_year: node.birth_year || "", death_year: node.death_year || "",
      bio: node.bio || "", photo: node.photo || null, media: node.media || [],
      is_self: !!node.is_self,
    });
    setOpen(true);
  };

  const onNodeMove = async (id, x, y) => {
    setNodes((ns) => (ns || []).map((n) => (n.id === id ? { ...n, x, y } : n)));
    try { await api.put(`/ancestors/${id}`, { x, y }); } catch { /* silent */ }
  };

  const onCreateLink = async (a, b) => {
    try {
      const res = await api.post("/family-links", { a, b });
      setLinks((ls) => (ls.some((l) => l.id === res.data.id) ? ls : [...ls, res.data]));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel collegamento");
    }
  };

  const onDeleteLink = async (lid) => {
    setLinks((ls) => ls.filter((l) => l.id !== lid));
    try { await api.delete(`/family-links/${lid}`); } catch { loadLinks(); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("photo");
    try {
      const data = await uploadBlob(file, file.name);
      set("photo", data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento");
    } finally { setBusy(null); if (photoRef.current) photoRef.current.value = ""; }
  };

  const addMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy("media");
    try {
      for (const file of files) {
        const data = await uploadBlob(file, file.name);
        setForm((f) => ({ ...f, media: [...f.media, data] }));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento");
    } finally { setBusy(null); if (mediaRef.current) mediaRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome"); return; }
    setSaving(true);
    try {
      if (mode === "create") {
        const { x, y } = centerPos();
        await api.post("/ancestors", {
          name: form.name, relation: form.relation || "",
          birth_year: form.birth_year || null, death_year: form.death_year || null,
          bio: form.bio, photo: form.photo, media: form.media, x, y,
          person_heir_id: form.person_heir_id || null,
        });
        toast.success("Persona aggiunta all'albero");
      } else {
        await api.put(`/ancestors/${form.id}`, {
          name: form.name, relation: form.relation || "",
          birth_year: form.birth_year || null, death_year: form.death_year || null,
          bio: form.bio, photo: form.photo, media: form.media,
        });
        toast.success("Scheda aggiornata");
      }
      setOpen(false);
      loadNodes();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Rimuovere questa persona dall'albero? I collegamenti verranno eliminati.")) return;
    try {
      await api.delete(`/ancestors/${form.id}`);
      toast.success("Rimosso dall'albero");
      setOpen(false);
      loadNodes();
      loadLinks();
    } catch (err) {
      toast.error("Errore nella rimozione");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="max-w-6xl mx-auto w-full px-6 pt-8 pb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#EAF3EE] flex items-center justify-center">
              <TreePine className="w-6 h-6 text-[#4e8f6f]" />
            </div>
            <div>
              <h1 className="font-serif text-4xl tracking-tight">Albero di Famiglia</h1>
              <p className="text-muted-foreground mt-1 max-w-2xl">Costruisci il tuo albero come vuoi: aggiungi una persona, indica chi è, trascina il riquadro dove preferisci e collega i familiari tra loro con una linea.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={openPeople} variant="outline" className="rounded-full h-11 px-5 border-border bg-white hover:bg-[#F4F1EA]" data-testid="add-from-people-btn">
              <Users className="w-4 h-4 mr-2" /> Da "Le tue persone"
            </Button>
            <Button onClick={onAddNew} className="rounded-full bg-primary hover:bg-[#b0553d] text-white h-11 px-5" data-testid="add-node-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo
            </Button>
          </div>
        </div>
      </div>

      <div className="relative border-t border-border overflow-hidden" style={{ height: "calc(100vh - 210px)", minHeight: 460, background: "radial-gradient(circle at 50% 50%, #F4F1EA 0%, #F9F6F0 60%)" }} data-testid="family-tree-canvas">
        <div className="absolute inset-0 pointer-events-none z-0" style={{ backgroundImage: `url(${TREE_BG})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.6, mixBlendMode: "multiply" }} aria-hidden="true" />
        {nodes === null ? (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">Caricamento…</div>
        ) : (
          <FamilyTree
            nodes={nodes}
            links={links}
            onNodeClick={onNodeClick}
            onNodeMove={onNodeMove}
            onCreateLink={onCreateLink}
            onDeleteLink={onDeleteLink}
          />
        )}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="bg-[#F9F6F0] w-full sm:max-w-md overflow-y-auto" data-testid="ancestor-sheet">
          <SheetHeader>
            <SheetTitle className="font-serif text-2xl">
              {mode === "create" ? "Aggiungi una persona" : form.is_self ? "La tua casella" : "Scheda"}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            <div className="flex flex-col items-center gap-3">
              <div className="w-28 h-28 rounded-full overflow-hidden border border-[#e3ddd3] bg-[#F4F1EA] flex items-center justify-center">
                {form.photo
                  ? <AuthMedia item={form.photo} className="w-full h-full object-cover" />
                  : <ImageIcon className="w-8 h-8 text-[#c9a892]" />}
              </div>
              <button onClick={() => photoRef.current?.click()} disabled={busy === "photo"} className="inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="ancestor-photo-btn">
                {busy === "photo" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {form.photo ? "Cambia foto" : "Aggiungi foto"}
              </button>
              <input ref={photoRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="ancestor-photo-input" />
            </div>

            <div>
              <Label className="mb-1.5 block">Nome</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nome e cognome" className="bg-white border-border rounded-xl h-11" data-testid="ancestor-name-input" />
            </div>

            <div>
              <Label className="mb-1.5 block">Parentela</Label>
              <Input
                value={form.relation}
                onChange={(e) => set("relation", e.target.value)}
                placeholder="Es. Nonno paterno, Zia…"
                list="relation-options"
                className="bg-white border-border rounded-xl h-11"
                data-testid="ancestor-relation-input"
              />
              <datalist id="relation-options">
                {RELATIONS.map((r) => <option key={r} value={r} />)}
              </datalist>
              <p className="text-xs text-muted-foreground mt-1">Scegli dall'elenco o scrivi liberamente.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Anno di nascita</Label>
                <Input value={form.birth_year} onChange={(e) => set("birth_year", e.target.value)} placeholder="1935" className="bg-white border-border rounded-xl h-11" data-testid="ancestor-birth-input" />
              </div>
              <div>
                <Label className="mb-1.5 block">Anno di morte</Label>
                <Input value={form.death_year} onChange={(e) => set("death_year", e.target.value)} placeholder="2008" className="bg-white border-border rounded-xl h-11" data-testid="ancestor-death-input" />
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block">Racconto / biografia</Label>
              <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={6} placeholder="Chi era, com'era, aneddoti, valori, mestiere…" className="bg-white border-border rounded-xl resize-none" data-testid="ancestor-bio-input" />
            </div>

            <div>
              <Label className="mb-2 block">Ricordi allegati (foto, audio, video, documenti)</Label>
              <div className="space-y-2">
                {form.media.map((m) => (
                  <div key={m.storage_path} className="flex items-center gap-3 bg-white rounded-xl p-2 border border-border">
                    <div className="flex-1 min-w-0">
                      {m.kind === "image"
                        ? <AuthMedia item={m} className="max-h-28 rounded-lg object-contain" />
                        : <AuthMedia item={m} />}
                    </div>
                    <button onClick={() => set("media", form.media.filter((x) => x.storage_path !== m.storage_path))} className="p-1.5 rounded-full hover:bg-destructive hover:text-white transition-colors" data-testid="ancestor-media-remove">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={() => mediaRef.current?.click()} disabled={busy === "media"} className="mt-2 inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="ancestor-media-btn">
                {busy === "media" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Allega un ricordo
              </button>
              <input ref={mediaRef} type="file" accept="image/*,video/*,audio/*,application/pdf" multiple onChange={addMedia} className="hidden" data-testid="ancestor-media-input" />
            </div>

            <div className="flex items-center gap-3 pt-2 pb-8">
              <Button onClick={save} disabled={saving} className="flex-1 rounded-full bg-primary hover:bg-[#b0553d] text-white h-11" data-testid="ancestor-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Salva
              </Button>
              {mode === "edit" && !form.is_self && (
                <Button onClick={remove} variant="ghost" className="rounded-full text-destructive hover:bg-destructive/10 h-11" data-testid="ancestor-delete-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={peopleOpen} onOpenChange={setPeopleOpen}>
        <DialogContent className="bg-[#F9F6F0] sm:max-w-md" data-testid="people-picker">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Aggiungi da "Le tue persone"</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">Scegli un familiare già salvato per aggiungerlo all'albero con un clic.</p>
          <div className="mt-3 max-h-[55vh] overflow-y-auto space-y-2 pr-1">
            {people.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 text-sm">
                Nessuna persona salvata. Aggiungile dalla pagina <a href="/persone" className="text-primary underline">Le tue persone</a>.
              </div>
            ) : (
              people.map((p) => {
                const added = (nodes || []).some((n) => n.person_heir_id === p.heir_id);
                return (
                  <div key={p.heir_id} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-border" data-testid={`person-row-${p.heir_id}`}>
                    <div className="w-10 h-10 rounded-full bg-[#EAF3EE] flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5 text-[#4e8f6f]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2C2A29] truncate">{p.name}</p>
                      {p.relationship && <p className="text-xs text-muted-foreground truncate">{p.relationship}</p>}
                    </div>
                    {added ? (
                      <span className="inline-flex items-center gap-1 text-xs text-[#4e8f6f] font-medium px-2"><Check className="w-4 h-4" /> Nell'albero</span>
                    ) : (
                      <Button size="sm" onClick={() => addFromPerson(p)} className="rounded-full bg-primary hover:bg-[#b0553d] text-white h-9" data-testid={`add-person-${p.heir_id}`}>
                        <UserPlus className="w-4 h-4 mr-1" /> Aggiungi
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlberoFamiglia;
